import {tracking} from "~/services/tracking.client";
import {marketingContext, analyticsContext} from "~/services/marketing.client";
import {ref} from "vue";
import {isValidPhone} from "~/services/helpers";
import type {checkoutInterface, DeliveryDataInterface, OrderRequestInterface,} from "~~/server/types/OrderInterface";
import type {CartInterface} from "~~/server/types/CartInterface";
import {useMessages} from "~/composables/useMessages";
import type {ConversionsInterface} from "~~/server/types/Products";

export function useOrder() {
  const localePath = useLocalePath();
  const {show_error}=useMessages();
  const { $api } = useNuxtApp()

  const cartStore = useCartStore()
  const orderStore=useOrdersStore();

  const delivery_data = ref<DeliveryDataInterface>({} as DeliveryDataInterface)
  let deliveryCalculation: AbortController | null = null
  const invalidateDelivery = () => {
    deliveryCalculation?.abort()
    deliveryCalculation = null
    calculateLoader.value = false
    delivery_data.value = {} as DeliveryDataInterface
  }

  const buildOrderRequest = (cart: CartInterface, currency:string,total: number, total_no_vat:number, form: checkoutInterface, deliveryData: DeliveryDataInterface): OrderRequestInterface => ({
        cart_id: cart.id,
        ...(cart.cart_token ? { cart_token: cart.cart_token } : {}),
        ...(cart.reservation_checkout_key ? { reservation_checkout_key: cart.reservation_checkout_key } : {}),
        ...(cart.subscription ? {
            subscription_checkout_key: cart.subscription_checkout_key,
            subscription_consent: form.subscription_consent === true,
            subscription_consent_version: cart.subscription.consent_version,
        } : {}),
        currency: cart.subscription?.currency ?? currency,
        total,
        total_no_vat,

        products: cart.products.map(i => {
            const conversion =
                i?.product?.conversions?.find((c: ConversionsInterface) => c.code === i.code)
                || {} as ConversionsInterface

            return {
                id: i.product_id,
                msd_code: i.product?.msd_code || '',
                cart_item_id: i.cart_item_id,
                name: i.product?.name || '',
                fragile: i.product?.fragile || 0,

                conversions: [
                    {
                        ...conversion,
                        count: conversion?.price ? i.count : 0,
                        price: i.price as number,
                        price_no_vat: i.price_no_vat as number,
                        total: i.total as number,
                        total_no_vat: i.total_no_vat as number,
                        active_discount: i.active_discount as number,
                    },
                ],
            }
        }),

        delivery_service: form.delivery_service,
        invoice_data: form.invoice_data,
        delivery_address: form.delivery_address,
        user_id: form.user_id,
        status_id: 0,
        terms: form.terms,

        ...deliveryData,

        payment_service: form.payment_service,

        user_data: {
            ...form.user_data,
            name:
                (form.user_data?.first_name || '') +
                ' ' +
                (form.user_data?.last_name || ''),
        },

        note: form.note,
      discount_code:form.discount_code,
      fast_order:form.fast_order
    });
  const placeOrderLoader = ref(false)
  const placeOrder = async (cart:CartInterface,order:OrderRequestInterface) => {
    placeOrderLoader.value = true
    deliveryCalculation?.abort()
    deliveryCalculation = null
    calculateLoader.value = false

    try {
      if (order.fast_order) tracking.beginCheckout(cart);
      if (order.delivery_service) tracking.shippingInfo(cart, order.delivery_service);
      if (order.payment_service) tracking.paymentInfo(cart, order.payment_service);
      const res = await orderStore.orderSave({ ...order, marketing_tracking: marketingContext(), analytics_tracking: analyticsContext() });
      if (useCookieConsent().consent.value.decided) {
        try {
          sessionStorage.setItem(`pending-purchase:${res.id}`, JSON.stringify({
            id: cart.id,
            saved_at: Date.now(),
            products: cart.products.map(item => ({
              product_id: item.product_id, count: item.count, price: item.price, total: item.total,
              options_meta: item.options_meta,
              product: { name: item.product.name, brand: item.product.brand, categories_list: item.product.categories_list },
            })),
          }));
        } catch { /* Storage may be unavailable. */ }
      }
      tracking.purchase(cart,res);

      await cartStore.clearCart(false);
      await navigateTo(localePath(`/pay/${order.payment_service}/${res.id}`));
    }
    catch (e) {
      await show_error(e);
      // Forbidden
      const err = e as {
        status?: number
        statusCode?: number
        response?: {
          status?: number
        _data?: {
            context?: {
              cart_item_id: number
            },
          title: string }
        }

      }

      const bookingError = (err.response?._data as any)?.data ?? err.response?._data
      if (err.response?.status === 409 && (bookingError as any)?.code === 'booking_unavailable') {
        cartStore.setItemError((bookingError as any)?.context?.cart_item_id || 0, (bookingError as any)?.message || '')
        await navigateTo(localePath('/cart'))
      }
      if (err.response?.status === 403) {
        const item_id=err.response?._data?.context?.cart_item_id || 0;
        cartStore.setItemError(item_id,err.response?._data?.title || '');
       await navigateTo(localePath("/cart"));
      }
    throw e;
    } finally {
      placeOrderLoader.value = false
    }
  }

  const tryCalculateDelivery = async (form:checkoutInterface,order:OrderRequestInterface) => {
    if (placeOrderLoader.value || !order.products.length) return
    invalidateDelivery()
    const address = form.delivery_address
    const user = form.user_data
    if(!address || !user) return

    const delivery_to = address.delivery_to
    const service = form.delivery_service
    const post_code = address.post_code
    const office_id = address.office_id

    const quarter = address.quarter
    const street = address.street

    const first_name = user?.first_name
    const last_name = user?.last_name
    const phone = user?.phone
    const email = user?.email

    const hasUser = !!first_name && !!last_name && !!phone?.trim() && isValidPhone(phone) && !!email
    const hasAddress = delivery_to === 'address' && (!!quarter || !!street) // оставям твоята логика
    const hasOffice = delivery_to === 'office' &&  !!office_id

    const can = !!service && hasUser && !!post_code && (hasAddress || hasOffice)
    if (!can || !isValidPhone(order.invoice_data?.phone)) return

    await calculateDelivery(order)
  }

  const calculateLoader = ref(false)
  const calculateDelivery = async (order:OrderRequestInterface) => {
    deliveryCalculation?.abort()
    const calculation = new AbortController()
    deliveryCalculation = calculation
    calculateLoader.value = true
    try {
      const result = await $api<DeliveryDataInterface>('calculate-delivery', {
        method: 'POST',
        body: order,
        signal: calculation.signal,
      })
      if (!calculation.signal.aborted) delivery_data.value = result
    } catch (e) {
      if (calculation.signal.aborted) return
      await show_error(e)
      delivery_data.value = {} as DeliveryDataInterface
    } finally {
      if (deliveryCalculation === calculation) {
        calculateLoader.value = false
        deliveryCalculation = null
      }
    }
  }



  return { 
    placeOrderLoader,
    calculateLoader,
    delivery_data,
    invalidateDelivery,
     buildOrderRequest,
    placeOrder,
    tryCalculateDelivery
  };
  
}
